import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { TFunction, useTranslation } from 'react-i18next'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { ErrorView, Loader, FieldInput, PercentButton } from '@percent/admin-dashboard/common/components'
import styles from './PartnerNameForm.module.scss'
import { ConfirmationModal, Grid, Modal, Text } from '@percent/lemonade'

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    name: Yup.string().required(t('typography.required')).max(255, t('errorMessage.shouldNotExceed255'))
  })

type PartnerNameFormType = {
  partnerId: string
  partnerName: string
  refreshApi: () => void
  editEnabled: boolean
}

export function PartnerNameForm({ partnerId, partnerName, refreshApi, editEnabled }: Readonly<PartnerNameFormType>) {
  const { adminService } = useServices()
  const [{ isLoading, errorMessage, success }, { apiFunc }] = useMutation(
    adminService.commands.editPartnerDetail,
    () => {
      refreshApi()
    }
  )
  const { t } = useTranslation()
  const [pendingName, setPendingName] = useState<string | null>(null)

  const { values, errors, handleBlur, handleSubmit, touched, setFieldValue } = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: partnerName || ''
    },
    validationSchema: validationSchema(t),
    onSubmit: ({ name }) => setPendingName(name)
  })

  const confirmNameChange = () => {
    if (pendingName === null) return
    setPendingName(null)
    apiFunc({
      payload: {
        name: pendingName
      },
      partnerId
    })
  }

  const addToast = useToast()

  useEffect(() => {
    if (success) {
      addToast(t('typography.partnerNameUpdated'), 'success')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success])

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Grid>
      <Grid.Col span={12}>
        <form style={{ margin: '24px 0 0' }} onSubmit={handleSubmit}>
          <Grid>
            <Grid.Col span={6} className={styles.input}>
              <FieldInput
                name="name"
                label={t('form.partnerName')}
                placeHolder={t('form.enterPartnerName')}
                value={values.name}
                onChange={a => setFieldValue('name', a)}
                error={errors?.name || ''}
                touched={touched.name}
                handleBlur={handleBlur}
                disabled={!editEnabled}
              />
            </Grid.Col>
            {editEnabled && (
              <Grid.Col span={5}>
                <div aria-hidden className={styles.labelSpacer} />
                <PercentButton
                  type="submit"
                  disabled={partnerName === values.name}
                  testId="save-changes-button"
                  title={t('button.saveChanges')}
                  className={styles.saveButton}
                />
              </Grid.Col>
            )}
          </Grid>
        </form>
        <Modal open={pendingName !== null} onClose={() => setPendingName(null)}>
          <ConfirmationModal
            title={t('dialog.editPartnerName.title')}
            primaryButtonText={t('button.confirm')}
            secondaryButtonText={t('button.cancel')}
            handleSubmit={confirmNameChange}
            handleClose={() => setPendingName(null)}
            primaryBtnTestId="confirm-partner-name-button"
            secondaryBtnTestId="cancel-partner-name-button"
          >
            <Text>{t('dialog.editPartnerName.description', { oldName: partnerName, newName: pendingName })}</Text>
          </ConfirmationModal>
        </Modal>
        {errorMessage && <ErrorView errorMessage={errorMessage} />}
      </Grid.Col>
    </Grid>
  )
}
